import { isAutomatedBrowser } from "./automation";

/**
 * Browser analytics is enabled only for a production Vite build carrying both
 * explicit production markers from the production deployment configuration.
 *
 * Neither marker is inferred from MODE/DEV/PROD: preview builds can also be
 * production-mode Vite builds. Requiring the deployment marker separately
 * prevents a staging build with accidental provider credentials from loading
 * direct third-party pixels. Missing and unknown values remain disabled.
 */
export function isProductionAnalyticsEnabled(): boolean {
  return (
    String(import.meta.env.VITE_ANALYTICS_ENV ?? "").trim().toLowerCase() ===
      "production" &&
    String(import.meta.env.VITE_DEPLOYMENT_ENV ?? "")
      .trim()
      .toLowerCase() === "production"
  );
}

/**
 * Fire a minimal direct PostHog capture payload for boot-time diagnostics that
 * cannot wait for the consent-gated SDK. Keeping this beside the environment
 * decision ensures watchdog beacons cannot drift around the production gate.
 */
export function captureProductionPostHogBeacon(
  event: string,
  properties: Record<string, unknown>,
): void {
  try {
    if (!isProductionAnalyticsEnabled()) return;
    if (isAutomatedBrowser()) return;
    const key = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
    if (!key || typeof fetch !== "function") return;
    const base = import.meta.env.BASE_URL || "/";
    void fetch(`${base}api/_a/ph/capture/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ api_key: key, event, properties }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* diagnostics must never affect app boot or consent */
  }
}

const CONSENT_DECISION_ID_KEY = "bac_consent_decision_id_v1";

function consentDecisionId(): string {
  try {
    const existing = sessionStorage.getItem(CONSENT_DECISION_ID_KEY);
    if (existing) return existing;
    const generated =
      globalThis.crypto?.randomUUID?.() ??
      `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem(CONSENT_DECISION_ID_KEY, generated);
    return generated;
  } catch {
    return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

/**
 * Essential, personless decision measurement. This intentionally records no
 * URL, user/account id, campaign id, or browser fingerprint. PostHog's request
 * geo supplies the coarse country source needed for an AU/NZ visitor-level
 * denominator, including reject-all and GPC decisions that cannot use the
 * consent-gated analytics SDK.
 */
export function captureWebMarketingConsentDecision(input: {
  choice: "accept_all" | "reject_all" | "save";
  marketingGranted: boolean;
  gpc: boolean;
}): void {
  captureProductionPostHogBeacon("web_marketing_consent_decided", {
    distinct_id: `consent-decision:${consentDecisionId()}`,
    $process_person_profile: false,
    platform: "web",
    choice: input.choice,
    marketing_granted: input.marketingGranted,
    gpc: input.gpc,
    country_source: "posthog_request_geo",
  });
}